import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useGlassDialog } from './glass-dialog';

interface GlassButtonProps {
  variant?: 'primary' | 'secondary' | 'ghost' | 'highlight';
  size?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseDown?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  type?: 'button' | 'submit' | 'reset';
  closeDialog?: boolean; // New prop to indicate if this button should close the dialog
}

export const GlassButton = ({
  variant = 'primary',
  size = 'md',
  className,
  children,
  loading,
  disabled,
  onClick,
  onMouseDown,
  type = 'button',
  closeDialog = false,
}: GlassButtonProps) => {
  const glassDialogContext = useGlassDialog();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    // If this button should close the dialog and we're in a GlassDialog context
    if (closeDialog && glassDialogContext) {
      glassDialogContext.handleClose();
    } else if (onClick) {
      onClick(event);
    }
  };
  const baseClasses =
    'relative font-inter font-light tracking-wide transition-all duration-200 backdrop-blur-xl border rounded-2xl overflow-hidden';

  const variants = {
    primary:
      'bg-white/15 hover:bg-white/25 text-white/95 border-white/30 hover:border-white/50 shadow-lg hover:shadow-xl',
    secondary:
      'bg-white/5 hover:bg-white/15 text-white/70 hover:text-white/95 border-white/20 hover:border-white/40',
    ghost:
      'bg-transparent hover:bg-white/10 text-white/60 hover:text-white/90 border-white/10 hover:border-white/20',
    highlight:
      'bg-blue-500/20 hover:bg-blue-500/30 text-blue-200 hover:text-blue-100 border-blue-400/30 hover:border-blue-400/50 shadow-lg hover:shadow-blue-500/20',
  };

  const sizes = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-sm',
    lg: 'px-8 py-4 text-base',
  };

  return (
    <motion.button
      type={type}
      onClick={handleClick}
      onMouseDown={onMouseDown}
      className={cn(
        baseClasses,
        variants[variant],
        sizes[size],
        (disabled || loading) && 'opacity-50 cursor-not-allowed',
        className,
      )}
      whileHover={!(disabled || loading) ? { scale: 1.02, y: -1 } : undefined}
      whileTap={!(disabled || loading) ? { scale: 0.98 } : undefined}
      transition={{ duration: 0.15 }}
      disabled={disabled || loading}
    >
      {/* Glass highlight overlay */}
      <div className="absolute inset-0 rounded-2xl bg-gradient-to-b from-white/10 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-200 pointer-events-none" />

      {/* Content */}
      <span className="relative flex items-center justify-center gap-2">
        {loading && (
          <div className="w-4 h-4 border border-white/30 border-t-white rounded-full animate-spin" />
        )}
        {children}
      </span>
    </motion.button>
  );
};
